import { Delete } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGetLocalityMaintenanceTemplates } from 'src/api/maintenanceTemplate'
import { GridActionButton } from 'src/components/common/GridActionButton'
import BasicDatePicker from 'src/components/DateTimePicker'
import { MainButton } from 'src/components/MainButton'
import { SelectField } from 'src/components/SelectField'
import { ISimpleMaintenanceTemplate } from 'src/generated-types/simple-maintenance-template'
import { Flex } from 'src/styles/flexComponent'
import { Padding } from 'src/styles/paddingComponent'
import { coreUtils } from 'src/utils'
import { getUTCDate } from 'src/utils/function'
import { z } from 'zod'
type FormSchema = z.infer<any>

interface Props {
  customerId?: number
  initialValues?: any
  form: UseFormReturn<FormSchema>
}

function LocalityFormMaintenances(props: Props) {
  const { customerId, form } = props
  const { t: translate } = useTranslation(['locality', 'common'])
  const currentDate = getUTCDate(new Date())
  const [maintenanceAssign, setMaintenanceAssign] = useState<{
    startDate: string | null
    repeatEnds: string | null
    name: string
    maintenanceTemplateId: number | null
  }>({
    startDate: getUTCDate(new Date())?.toUTCString() || null,
    repeatEnds: null,
    name: '',
    maintenanceTemplateId: null,
  })
  const [startDateError, setStartDateError] = useState<null | string>(null)
  const [maintenanceTemplateError, setMaintenanceTemplateError] = useState<null | string>(null)

  const { data: maintenanceTemplates } = useGetLocalityMaintenanceTemplates(customerId as EntityId)

  const onAddMaintenanceTemplate = () => {
    setStartDateError(null)
    setMaintenanceTemplateError(null)
    if (maintenanceAssign.maintenanceTemplateId === null || maintenanceAssign.maintenanceTemplateId === undefined) {
      setMaintenanceTemplateError(translate('required', { ns: 'common' }))
    } else if (
      maintenanceAssign.startDate === null ||
      maintenanceAssign.startDate === undefined ||
      (currentDate && new Date(maintenanceAssign.startDate) < new Date(currentDate))
    ) {
      setStartDateError(translate('startDateWithDateNowValidation', { ns: 'common' }))
    } else if (
      maintenanceAssign.repeatEnds != null &&
      new Date(maintenanceAssign.startDate) >
      new Date(maintenanceAssign.repeatEnds)
    ) {
      setStartDateError(translate('endStartDateError', { ns: 'common' }))
    } else {
      setStartDateError(null)
      form.setValue('localityMaintenanceTemplates', [
        ...(form.watch('localityMaintenanceTemplates') ?? []),
        {
          id: 0,
          name: maintenanceAssign.name,
          startDate: maintenanceAssign.startDate,
          repeatEnds: maintenanceAssign.repeatEnds,
          maintenanceTemplateId: maintenanceAssign.maintenanceTemplateId,
        },
      ])
      setMaintenanceAssign({
        startDate: getUTCDate(new Date())?.toUTCString() || null,
        repeatEnds: null,
        name: '',
        maintenanceTemplateId: null,
      })
    }
  }

  return (
    <>
      <Box
        sx={theme => ({
          border: '1px solid',
          borderColor: theme.palette.secondaryLight.main,
          padding: 2,
          borderRadius: 1,
          marginY: 1,
          width: '98%',
          marginLeft: '8px',
        })}
      >
        <Typography variant="body1" sx={{ paddingBottom: '12px', paddingLeft: '8px' }}>
          {translate('locality-templates', { ns: 'maintenance' })}
        </Typography>
        <Padding.ph8>
          <Flex.Row style={{ borderBottom: '1px solid', borderColor: '#808080' }}>
            <Padding.p6 flex={1}>{translate('maintenance-template', { ns: 'maintenance' })}</Padding.p6>
            <Padding.p6 flex={1}>{translate('startDate', { ns: 'common' })}</Padding.p6>
            <Padding.p6 flex={1}>{translate('repeat-ends', { ns: 'maintenance' })}</Padding.p6>
            <Box sx={{ width: '80px' }}></Box>
          </Flex.Row>
          {form.watch('localityMaintenanceTemplates')?.map((template, index) => {
            return (
              <Flex.Row key={template.id} style={{ borderBottom: '1px solid', borderColor: '#E0E0E0' }} paddingY={1}>
                <Padding.p6 flex={1}>
                  <Typography variant="body2">{template.name}</Typography>
                </Padding.p6>
                <Padding.p6 flex={1}>
                  <Typography variant="body2">{coreUtils.getFormattedDate(template.startDate)}</Typography>
                </Padding.p6>
                <Padding.p6 flex={1}>
                  <Typography variant="body2">{coreUtils.getFormattedDate(template.repeatEnds)}</Typography>
                </Padding.p6>
                <Box sx={{ width: '80px' }}>
                  <GridActionButton
                    width="40px"
                    buttonIcon={<Delete color="error" />}
                    onClick={() => {
                      const updatedTemplates = form.watch('localityMaintenanceTemplates')?.filter((_, i) => i !== index)
                      form.setValue('localityMaintenanceTemplates', updatedTemplates)
                    }}
                  />
                </Box>
              </Flex.Row>
            )
          })}
        </Padding.ph8>
        <Flex.Row sx={{ paddingTop: '6px' }}>
          <Box flex={1}>
            <SelectField
              data={maintenanceTemplates || []}
              label={translate('maintenance-template', { ns: 'maintenance' })}
              getOptionLabel={option => option.name}
              value={
                maintenanceTemplates?.find(
                  (item: ISimpleMaintenanceTemplate) => item.id === maintenanceAssign?.maintenanceTemplateId,
                ) ?? null
              }
              renderOption={(props: any, option) => (
                <Box {...props} key={option.id}>
                  {option.name}
                </Box>
              )}
              onChange={(_, value) => {
                const currentValue = value as ISimpleMaintenanceTemplate
                setMaintenanceAssign(v => ({
                  ...v,
                  ...{ name: currentValue?.name, maintenanceTemplateId: currentValue?.id },
                }))
              }}
              clearOnBlur={false}
              clearOnEscape={false}
              isOptionEqualToValue={(o, v) => o.id === v.id}
            />
            <Typography
              variant="caption"
              color="error"
              sx={{
                paddingLeft: '8px',
                display: maintenanceTemplateError ? 'block' : 'none',
              }}
            >
              {maintenanceTemplateError}
            </Typography>
          </Box>
          <Box flex={1}>
            <BasicDatePicker
              isWithoutAppForm={true}
              name="startDateValue"
              value={maintenanceAssign?.startDate}
              defaultValue={getUTCDate(new Date())}
              label={translate('startDate', { ns: 'common' })}
              onChange={startDate => {
                setStartDateError(null)
                setMaintenanceAssign(v => ({
                  ...v,
                  ...{ startDate: startDate ? getUTCDate(startDate)?.toUTCString() || null : startDate },
                }))
              }}
            />
            <Typography
              variant="caption"
              color="error"
              sx={{
                paddingLeft: '8px',
                display: startDateError ? 'block' : 'none',
              }}
            >
              {startDateError}
            </Typography>
          </Box>
          <Box flex={1}>
            <BasicDatePicker
              name="repeatEnd"
              isWithoutAppForm={true}
              value={maintenanceAssign?.repeatEnds}
              label={translate('repeat-ends', { ns: 'maintenance' })}
              onChange={repeatEnds => {
                setMaintenanceAssign(v => ({
                  ...v,
                  ...{ repeatEnds: repeatEnds ? getUTCDate(repeatEnds)?.toUTCString() || null : null },
                }))
              }}
            />
          </Box>
          <Box sx={{ width: '80px' }}>
            <MainButton
              variant="contained"
              color="primary"
              size="small"
              sx={{ marginTop: '16px', marginLeft: '8px' }}
              onClick={onAddMaintenanceTemplate}
            >
              {translate('add', { ns: 'common' })}
            </MainButton>
          </Box>
        </Flex.Row>
      </Box>
    </>
  )
}

export default LocalityFormMaintenances
